import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Eye, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  qrtApplyInvoiceBilling,
  qrtDownloadInvoicesCsv,
  qrtDownloadInvoicesZip,
  qrtDownloadUserInvoice,
  qrtGetUserInvoice,
  qrtGetUserInvoicePdf,
  qrtListInvoiceCompanies,
  qrtListInvoices,
} from "../../apis/qrtAdmin";
import { Pager, Spinner, StatCard, StatusChip, fmtMoney, num } from "./qrtUi";
import { InvoicePreviewModal } from "./QrtUserInvoices";

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
const PAGE_SIZE = 25;
const TYPE_LABELS = {
  plan: "Plan",
  addon: "Add-on",
  ai_credits: "AI credits",
  wallet: "Wallet",
};

function istIso(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function shiftIso(iso, days) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function rangeFor(preset) {
  const today = istIso();
  if (preset === "month") return { from: `${today.slice(0, 8)}01`, to: today };
  if (preset === "last_month") {
    const last = shiftIso(`${today.slice(0, 8)}01`, -1);
    return { from: `${last.slice(0, 8)}01`, to: last };
  }
  return { from: "", to: "" };
}

async function readError(err, fallback) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.error || fallback;
    } catch (_) {
      return fallback;
    }
  }
  return data?.error || err.message || fallback;
}

const companyLabel = (company) => {
  const name = company.legal_business_name || company.name || company.email;
  return `${name} · ${company.email || ""}`;
};

const QrtInvoices = () => {
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [query, setQuery] = useState("");
  const [gst, setGst] = useState("all");
  const [address, setAddress] = useState("all");
  const [type, setType] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [gstin, setGstin] = useState("");
  const [billAddress, setBillAddress] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);

  const filters = useMemo(() => {
    const params = { gst, address };
    if (from) params.from = from;
    if (to) params.to = to;
    if (query.trim()) params.q = query.trim();
    if (type) params.type = type;
    return params;
  }, [from, to, query, gst, address, type]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qrtListInvoices({ ...filters, page: page + 1, limit: PAGE_SIZE });
      setData(res.data?.data || null);
    } catch (err) {
      setData(null);
      toast.error(await readError(err, "Failed to load invoices"));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    qrtListInvoiceCompanies()
      .then((res) => setCompanies(res.data?.data || []))
      .catch(() => setCompanies([]));
  }, []);

  const selected = companies.find((c) => String(c.id) === String(companyId)) || null;

  const chooseCompany = (id) => {
    setCompanyId(String(id || ""));
    const company = companies.find((c) => String(c.id) === String(id));
    if (!company) return;
    setGstin(company.billing_gstin || "");
    setBillAddress(company.billing_address || "");
    setCompanyName(company.billing_company_name || company.legal_business_name || company.name || "");
  };

  const applyPreset = (id) => {
    const range = rangeFor(id);
    setPreset(id);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };

  const applyBilling = async (e) => {
    e.preventDefault();
    if (!companyId) {
      toast.error("Choose a company first");
      return;
    }
    const cleanGst = String(gstin || "").trim().toUpperCase().replace(/\s+/g, "");
    if (cleanGst && !GSTIN_RE.test(cleanGst)) {
      toast.error("Enter a valid 15-character GST number, or leave it blank.");
      return;
    }
    if (!cleanGst && !billAddress.trim() && !companyName.trim()) {
      toast.error("Enter a GST number, an address, or a company name.");
      return;
    }
    const count = Number(selected?.invoice_count || 0);
    const ok = window.confirm(
      `Write these details onto all ${count} invoice${count === 1 ? "" : "s"} for ${companyLabel(selected)}?`
    );
    if (!ok) return;
    setSaving(true);
    try {
      const res = await qrtApplyInvoiceBilling({
        user_id: Number(companyId),
        customer_gstin: cleanGst,
        customer_address: billAddress.trim(),
        customer_company_name: companyName.trim(),
      });
      toast.success(`Updated ${res.data?.data?.updated || 0} invoices`);
      const companiesRes = await qrtListInvoiceCompanies();
      setCompanies(companiesRes.data?.data || []);
      load();
    } catch (err) {
      toast.error(await readError(err, "Failed to update invoices"));
    } finally {
      setSaving(false);
    }
  };

  const download = async (kind) => {
    setExporting(kind);
    try {
      if (kind === "csv") await qrtDownloadInvoicesCsv(filters);
      else await qrtDownloadInvoicesZip(filters);
      toast.success(kind === "csv" ? "Spreadsheet downloaded" : "Invoice PDFs downloaded");
    } catch (err) {
      toast.error(await readError(err, "Download failed"));
    } finally {
      setExporting("");
    }
  };

  const downloadOne = async (invoice) => {
    setBusyId(invoice.id);
    try {
      await qrtDownloadUserInvoice(invoice.user_id, invoice, `${invoice.invoice_number}.pdf`);
    } catch (_) {
      toast.error("Failed to download invoice");
    } finally {
      setBusyId(null);
    }
  };

  const handlePreview = async (invoice) => {
    setBusyId(invoice.id);
    try {
      const [detailRes, pdfRes] = await Promise.all([
        qrtGetUserInvoice(invoice.user_id, invoice.id),
        qrtGetUserInvoicePdf(invoice.user_id, invoice.id),
      ]);
      const blobUrl = URL.createObjectURL(new Blob([pdfRes.data], { type: "application/pdf" }));
      setPreview({
        userId: invoice.user_id,
        invoice: detailRes.data?.data || invoice,
        blobUrl,
      });
    } catch (_) {
      toast.error("Failed to open invoice preview");
    } finally {
      setBusyId(null);
    }
  };

  const closePreview = () => {
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  };

  const summary = data?.summary || {};

  return (
    <>
      <div className="qrt-page-head">
        <div className="grow">
          <h2>Invoices</h2>
          <p>Filter every invoice, download them for your CA, and fill GST and address for a whole company at once.</p>
        </div>
      </div>

      <div className="qrt-card" style={{ marginBottom: 16 }}>
        <div className="qrt-card-head">
          <h3>GST and address for one company</h3>
        </div>
        <form onSubmit={applyBilling} style={{ padding: "0 16px 16px" }}>
          <div className="qrt-bill-form">
            <label>
              Company
              <select value={companyId} onChange={(e) => chooseCompany(e.target.value)}>
                <option value="">Choose a company…</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {companyLabel(company)} ({company.invoice_count})
                  </option>
                ))}
              </select>
            </label>
            <label>
              GST number
              <input
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase())}
                placeholder="24AAAAA0000A1Z5"
                maxLength={15}
              />
            </label>
            <label>
              Address
              <textarea
                value={billAddress}
                onChange={(e) => setBillAddress(e.target.value)}
                placeholder="Billing address printed on the invoice"
              />
            </label>
            <button type="submit" className="ping-btn ping-btn-green" disabled={saving}>
              {saving ? "Saving…" : "Save on all invoices"}
            </button>
          </div>
          <label style={{ display: "block", marginTop: 10, maxWidth: 420 }}>
            <span style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Company name on invoice</span>
            <input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Legal name"
              style={{ width: "100%", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px" }}
            />
          </label>
          <p className="qrt-bill-note">
            {selected
              ? `${companyLabel(selected)} has ${num(selected.invoice_count)} invoices. ${num(selected.missing_gst)} are missing GST and ${num(selected.missing_address)} are missing an address. Filled fields replace those details on every invoice for this company, and new invoices will use them too. A blank field is left as it is.`
              : "Pick the company once. You do not need to open each invoice."}
          </p>
        </form>
      </div>

      <div className="qrt-stats">
        <StatCard label="Invoices" value={num(summary.count)} />
        <StatCard label="Total" value={fmtMoney(summary.total_amount)} />
        <StatCard label="Missing GST" value={num(summary.missing_gst)} />
        <StatCard label="Missing address" value={num(summary.missing_address)} />
      </div>

      <div className="qrt-card">
        <div className="qrt-card-head">
          <div className="qrt-range-picks" style={{ margin: 0 }}>
            {[
              ["all", "All dates"],
              ["month", "This month"],
              ["last_month", "Last month"],
              ["custom", "Custom"],
            ].map(([id, label]) => (
              <button key={id} type="button" className={preset === id ? "on" : ""} onClick={() => applyPreset(id)}>
                {label}
              </button>
            ))}
          </div>
          <div className="qrt-inline">
            {preset === "custom" && (
              <>
                <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} />
                <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} />
              </>
            )}
            <input
              type="search"
              placeholder="Invoice, company, email, GST"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(0); }}
            />
            <select value={gst} onChange={(e) => { setGst(e.target.value); setPage(0); }}>
              <option value="all">GST: all</option>
              <option value="missing">GST missing</option>
              <option value="present">GST present</option>
            </select>
            <select value={address} onChange={(e) => { setAddress(e.target.value); setPage(0); }}>
              <option value="all">Address: all</option>
              <option value="missing">Address missing</option>
              <option value="present">Address present</option>
            </select>
            <select value={type} onChange={(e) => { setType(e.target.value); setPage(0); }}>
              <option value="">All types</option>
              <option value="plan">Plan</option>
              <option value="wallet">Wallet</option>
              <option value="addon">Add-on</option>
              <option value="ai_credits">AI credits</option>
            </select>
            <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" disabled={!!exporting} onClick={() => download("csv")}>
              <FileSpreadsheet size={14} />
              {exporting === "csv" ? "Preparing…" : "Download sheet"}
            </button>
            <button type="button" className="ping-btn ping-btn-green ping-btn-sm" disabled={!!exporting} onClick={() => download("zip")}>
              <Download size={14} />
              {exporting === "zip" ? "Preparing PDFs…" : "Download PDFs"}
            </button>
          </div>
        </div>

        {loading ? <Spinner /> : (
          <table className="qrt-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Company</th>
                <th>GST</th>
                <th>Address</th>
                <th>Type</th>
                <th className="r">Amount</th>
                <th>Status</th>
                <th className="r">Date</th>
                <th className="r">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.invoices || []).map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{inv.invoice_number}</div>
                    <div className="sub">{inv.plan_name || "—"}</div>
                  </td>
                  <td>
                    <Link to={`/qrt/users/${inv.user_id}`}>{inv.customer_company_name || inv.legal_business_name || inv.user_name || "—"}</Link>
                    <div className="sub">{inv.customer_email || inv.user_email || ""}</div>
                  </td>
                  <td>{inv.customer_gstin || <span className="ping-chip a">Missing</span>}</td>
                  <td style={{ maxWidth: 220 }}>
                    {inv.customer_address
                      ? <span className="sub">{inv.customer_address}</span>
                      : <span className="ping-chip a">Missing</span>}
                  </td>
                  <td>{TYPE_LABELS[inv.type] || inv.type || "—"}</td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(inv.total)}</td>
                  <td><StatusChip value={inv.status || "paid"} /></td>
                  <td className="r sub">{inv.invoice_date || "—"}</td>
                  <td className="r">
                    <div className="qrt-inv-actions">
                      <button
                        type="button"
                        className="ping-btn ping-btn-ghost ping-btn-sm"
                        disabled={busyId === inv.id}
                        onClick={() => handlePreview(inv)}
                      >
                        {busyId === inv.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                        Preview
                      </button>
                      <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => chooseCompany(inv.user_id)}>
                        Fill company
                      </button>
                      <button type="button" className="ping-btn ping-btn-green ping-btn-sm" disabled={busyId === inv.id} onClick={() => downloadOne(inv)}>
                        <Download size={14} />
                        PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(data?.invoices || []).length === 0 && (
                <tr><td className="empty" colSpan={9}>No invoices match these filters</td></tr>
              )}
            </tbody>
          </table>
        )}
        <Pager page={page} total={data?.total || 0} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      {preview && (
        <InvoicePreviewModal
          userId={preview.userId || preview.invoice.user_id}
          preview={preview}
          onClose={closePreview}
          onSaved={async (updated, blobUrl) => {
            if (preview.blobUrl) URL.revokeObjectURL(preview.blobUrl);
            setPreview({
              userId: preview.userId || updated?.user_id,
              invoice: updated,
              blobUrl,
            });
            load();
            qrtListInvoiceCompanies()
              .then((res) => setCompanies(res.data?.data || []))
              .catch(() => {});
          }}
        />
      )}
    </>
  );
};

export default QrtInvoices;
